import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Play, TrendingUp, TrendingDown, Trophy, Target, GitCompareArrows, Download } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { downloadCsv, csvButtonBase } from "@/lib/csv";

const UNIVERSES = [
  { id: "bist30", label: "BIST 30" },
  { id: "bist100", label: "BIST 100" },
];
const HOLDINGS = [5, 10, 20];

export default function BacktestPage() {
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("golden_cross");
  const [compareId, setCompareId] = useState("");   // "" = disabled
  const [universe, setUniverse] = useState("bist30");
  const [holding, setHolding] = useState(10);
  const [months, setMonths] = useState(6);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultB, setResultB] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/preset-strategies").then((r) => setPresets(r.data.items || [])).catch(() => {});
  }, []);

  const run = async () => {
    setRunning(true);
    setResult(null); setResultB(null);
    try {
      const req = (pid) => api.post("/backtest", {
        preset_id: pid, universe, holding_days: holding, period_months: months,
      });
      if (compareId && compareId !== presetId) {
        const [ra, rb] = await Promise.all([req(presetId), req(compareId)]);
        setResult(ra.data); setResultB(rb.data);
        toast.success(`A: ${ra.data.total_signals} sinyal (%${ra.data.win_rate.toFixed(1)}) · B: ${rb.data.total_signals} sinyal (%${rb.data.win_rate.toFixed(1)})`);
      } else {
        const { data } = await req(presetId);
        setResult(data);
        toast.success(`${data.total_signals} sinyal, %${data.win_rate.toFixed(1)} kazanma oranı`);
      }
    } catch { toast.error("Backtest başarısız"); }
    finally { setRunning(false); }
  };

  const selectedPreset = presets.find((p) => p.id === presetId);
  const comparePreset = presets.find((p) => p.id === compareId);

  return (
    <div className="space-y-6" data-testid="backtest-page">
      <div>
        <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
          <FlaskConical size={22} className="text-brand-blue" /> Backtest Motoru
        </h1>
        <p className="text-sm text-text-secondary mt-1">Hazır stratejileri geçmişte test edin — kaç sinyal kazandırırdı?</p>
      </div>

      {/* Config panel */}
      <div className="bg-bg-panel border border-bg-border rounded-xl p-4 sm:p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Strateji A</label>
            <select
              data-testid="backtest-preset-select"
              value={presetId}
              onChange={(e) => setPresetId(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
            >
              {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1">
              <GitCompareArrows size={10} /> Strateji B (opsiyonel)
            </label>
            <select
              data-testid="backtest-compare-select"
              value={compareId}
              onChange={(e) => setCompareId(e.target.value)}
              className="w-full bg-bg-base border border-bg-border text-text-primary rounded-lg px-3 py-2 text-sm focus:border-brand-blue focus:outline-none"
            >
              <option value="">— Yalnız A —</option>
              {presets.filter((p) => p.id !== presetId).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Evren</label>
            <div className="flex gap-1 bg-bg-base p-1 rounded-lg border border-bg-border">
              {UNIVERSES.map((u) => (
                <button
                  key={u.id}
                  data-testid={`backtest-universe-${u.id}`}
                  onClick={() => setUniverse(u.id)}
                  className={`flex-1 py-1.5 text-xs rounded ${universe === u.id ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}
                >{u.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Tutma Süresi</label>
            <div className="flex gap-1 bg-bg-base p-1 rounded-lg border border-bg-border">
              {HOLDINGS.map((h) => (
                <button
                  key={h}
                  data-testid={`backtest-hold-${h}`}
                  onClick={() => setHolding(h)}
                  className={`flex-1 py-1.5 text-xs rounded ${holding === h ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}
                >{h} gün</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Geçmiş</label>
            <div className="flex gap-1 bg-bg-base p-1 rounded-lg border border-bg-border">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`flex-1 py-1.5 text-xs rounded ${months === m ? "bg-brand-blue text-white" : "text-text-secondary hover:text-text-primary"}`}
                >{m} ay</button>
              ))}
            </div>
          </div>
        </div>

        {selectedPreset && (
          <div className="text-xs text-text-secondary bg-bg-base border border-bg-border rounded-lg p-3">
            <span className="text-text-primary font-medium">{selectedPreset.name}:</span> {selectedPreset.description}
          </div>
        )}

        <button
          data-testid="backtest-run-btn"
          onClick={run}
          disabled={running}
          className="bg-accent-yellow text-bg-base font-bold px-6 py-2.5 rounded-lg hover:opacity-95 active:scale-[0.98] disabled:opacity-60 flex items-center gap-2 shadow-md"
        ><Play size={14} /> {running ? "Test ediliyor..." : "Backtest'i Çalıştır"}</button>
      </div>

      {running && <div className="p-16 flex justify-center"><div className="radar-loader" /></div>}

      {!running && result && !resultB && <BacktestResult result={result} nav={nav} />}

      {!running && result && resultB && (
        <CompareResults a={result} b={resultB} aName={selectedPreset?.name} bName={comparePreset?.name} nav={nav} />
      )}
    </div>
  );
}

function CompareResults({ a, b, aName, bName, nav }) {
  // Merge equity curves by date
  const dates = Array.from(new Set([...(a.equity_curve || []).map((p) => p.date), ...(b.equity_curve || []).map((p) => p.date)])).sort();
  const aMap = Object.fromEntries((a.equity_curve || []).map((p) => [p.date, p.cum_return_pct]));
  const bMap = Object.fromEntries((b.equity_curve || []).map((p) => [p.date, p.cum_return_pct]));
  let lastA = 0, lastB = 0;
  const merged = dates.map((d) => {
    if (aMap[d] != null) lastA = aMap[d];
    if (bMap[d] != null) lastB = bMap[d];
    return { date: d, A: Number(lastA.toFixed(2)), B: Number(lastB.toFixed(2)) };
  });

  const winner = a.win_rate > b.win_rate ? "A" : a.win_rate < b.win_rate ? "B" : "=";
  const retWinner = a.avg_return_pct > b.avg_return_pct ? "A" : a.avg_return_pct < b.avg_return_pct ? "B" : "=";

  return (
    <>
      {/* Head-to-head table */}
      <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden" data-testid="backtest-compare-table">
        <div className="px-4 py-3 border-b border-bg-border">
          <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <GitCompareArrows size={14} className="text-brand-blue" /> Kafa Kafaya Karşılaştırma
          </h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
              <th className="py-2.5 px-4 text-left">Metrik</th>
              <th className="py-2.5 px-4 text-right">A · {aName || "-"}</th>
              <th className="py-2.5 px-4 text-right">B · {bName || "-"}</th>
              <th className="py-2.5 px-4 text-center">Kazanan</th>
            </tr>
          </thead>
          <tbody>
            <CmpRow label="Toplam Sinyal" av={a.total_signals} bv={b.total_signals} />
            <CmpRow label="Kazanma Oranı" av={`${a.win_rate.toFixed(1)}%`} bv={`${b.win_rate.toFixed(1)}%`} winner={winner} />
            <CmpRow label="Ortalama Getiri" av={`${a.avg_return_pct >= 0 ? "+" : ""}${a.avg_return_pct.toFixed(2)}%`} bv={`${b.avg_return_pct >= 0 ? "+" : ""}${b.avg_return_pct.toFixed(2)}%`} winner={retWinner} pct />
            <CmpRow label="Ort. Kazanç" av={`+${a.avg_win_pct.toFixed(2)}%`} bv={`+${b.avg_win_pct.toFixed(2)}%`} />
            <CmpRow label="Ort. Kayıp" av={`${a.avg_loss_pct.toFixed(2)}%`} bv={`${b.avg_loss_pct.toFixed(2)}%`} />
            <CmpRow label="Kazanan / Kaybeden" av={`${a.wins} / ${a.losses}`} bv={`${b.wins} / ${b.losses}`} />
          </tbody>
        </table>
      </div>

      {/* Equity curve chart */}
      {merged.length > 0 && (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-4" data-testid="backtest-equity-chart">
          <h3 className="text-sm font-semibold text-text-primary mb-3">Kümülatif Getiri Eğrisi</h3>
          <div style={{ height: 340 }}>
            <ResponsiveContainer>
              <LineChart data={merged}>
                <CartesianGrid stroke="rgb(var(--bg-hover))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="rgb(var(--text-secondary))" tick={{ fontSize: 10 }} />
                <YAxis stroke="rgb(var(--text-secondary))" tick={{ fontSize: 10 }} unit="%" />
                <RTooltip
                  contentStyle={{ background: "rgb(var(--bg-panel))", border: "1px solid rgb(var(--bg-border))", borderRadius: 8, color: "rgb(var(--text-primary))" }}
                  formatter={(v) => `${v}%`}
                />
                <Legend wrapperStyle={{ color: "rgb(var(--text-secondary))", fontSize: 12 }} />
                <Line type="monotone" dataKey="A" name={aName || "A"} stroke="#2D6BFF" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="B" name={bName || "B"} stroke="#F5C542" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </>
  );
}

function CmpRow({ label, av, bv, winner, pct }) {
  const aWin = winner === "A", bWin = winner === "B";
  return (
    <tr className="border-b border-bg-border">
      <td className="py-2.5 px-4 text-text-secondary">{label}</td>
      <td className={`py-2.5 px-4 text-right font-mono ${aWin ? "text-signal-up font-semibold" : "text-text-primary"}`}>{av}</td>
      <td className={`py-2.5 px-4 text-right font-mono ${bWin ? "text-signal-up font-semibold" : "text-text-primary"}`}>{bv}</td>
      <td className="py-2.5 px-4 text-center text-xs font-semibold">
        {winner === "=" || !winner ? <span className="text-text-secondary">—</span> :
          <span className={aWin ? "text-brand-blue" : "text-accent-yellow"}>{winner}</span>}
      </td>
    </tr>
  );
}

function BacktestResult({ result, nav }) {
  const noSignals = result.total_signals === 0;
  const posAvg = result.avg_return_pct >= 0;
  return (
    <>
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile
          label="Toplam Sinyal"
          value={result.total_signals}
          sub={`${result.universe} · ${result.holding_days} gün tutma`}
          data-testid="bt-tile-signals"
        />
        <Tile
          label="Kazanma Oranı"
          value={`${result.win_rate.toFixed(1)}%`}
          sub={`${result.wins} kazanan / ${result.losses} kaybeden`}
          tone={result.win_rate >= 50 ? "up" : "down"}
          icon={Target}
          data-testid="bt-tile-winrate"
        />
        <Tile
          label="Ortalama Getiri"
          value={`${posAvg ? "+" : ""}${result.avg_return_pct.toFixed(2)}%`}
          sub={`İşlem başına`}
          tone={posAvg ? "up" : "down"}
          icon={posAvg ? TrendingUp : TrendingDown}
          data-testid="bt-tile-avgret"
        />
        <Tile
          label="Ort. Kazanç / Kayıp"
          value={
            <span className="text-sm">
              <span className="text-signal-up">+{result.avg_win_pct.toFixed(2)}%</span>
              <span className="text-text-secondary mx-1">/</span>
              <span className="text-signal-down">{result.avg_loss_pct.toFixed(2)}%</span>
            </span>
          }
          data-testid="bt-tile-winloss"
        />
      </div>

      {noSignals ? (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center">
          <FlaskConical size={28} className="text-text-secondary mx-auto mb-3" />
          <h3 className="text-text-primary font-semibold mb-1">Test döneminde sinyal üretilmedi</h3>
          <p className="text-sm text-text-secondary">Daha geniş bir evren veya daha uzun bir dönem seçebilirsiniz.</p>
        </div>
      ) : (
        <>
          {/* Best/Worst */}
          {(result.best_trade || result.worst_trade) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {result.best_trade && (
                <div className="bg-bg-panel border border-signal-up/40 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-signal-up flex items-center gap-1.5 mb-2">
                    <Trophy size={11} /> En İyi İşlem
                  </div>
                  <div className="flex items-center justify-between">
                    <button onClick={() => nav(`/stock/${result.best_trade.symbol}`)} className="text-left">
                      <div className="font-mono font-bold text-text-primary text-lg">{result.best_trade.symbol}</div>
                      <div className="text-[11px] text-text-secondary">{result.best_trade.entry_date} → {result.best_trade.exit_date}</div>
                    </button>
                    <div className="text-signal-up text-2xl font-bold font-mono">
                      +{result.best_trade.return_pct.toFixed(2)}%
                    </div>
                  </div>
                </div>
              )}
              {result.worst_trade && (
                <div className="bg-bg-panel border border-signal-down/40 rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-signal-down flex items-center gap-1.5 mb-2">
                    <TrendingDown size={11} /> En Kötü İşlem
                  </div>
                  <div className="flex items-center justify-between">
                    <button onClick={() => nav(`/stock/${result.worst_trade.symbol}`)} className="text-left">
                      <div className="font-mono font-bold text-text-primary text-lg">{result.worst_trade.symbol}</div>
                      <div className="text-[11px] text-text-secondary">{result.worst_trade.entry_date} → {result.worst_trade.exit_date}</div>
                    </button>
                    <div className="text-signal-down text-2xl font-bold font-mono">
                      {result.worst_trade.return_pct.toFixed(2)}%
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trade log */}
          <div className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-bg-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Sinyal Kaydı</h3>
                <p className="text-[11px] text-text-secondary">Son {Math.min(200, result.trades.length)} sinyal (yeniden eskiye)</p>
              </div>
              <button
                data-testid="backtest-csv"
                onClick={() => downloadCsv("ztan_backtest.csv", result.trades, ["symbol","entry_date","exit_date","entry_price","exit_price","return_pct"])}
                className={csvButtonBase()}
              ><Download size={12} /> CSV</button>
            </div>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-bg-panel">
                  <tr className="text-text-secondary text-xs uppercase tracking-wider border-b border-bg-border">
                    <th className="py-2.5 px-4 text-left">Hisse</th>
                    <th className="py-2.5 px-4 text-left">Giriş</th>
                    <th className="py-2.5 px-4 text-left">Çıkış</th>
                    <th className="py-2.5 px-4 text-right">Giriş Fiyatı</th>
                    <th className="py-2.5 px-4 text-right">Çıkış Fiyatı</th>
                    <th className="py-2.5 px-4 text-right">Getiri</th>
                  </tr>
                </thead>
                <tbody>
                  {result.trades.map((t, i) => (
                    <tr
                      key={i}
                      data-testid={`bt-trade-row-${i}`}
                      onClick={() => nav(`/stock/${t.symbol}`)}
                      className="border-b border-bg-border hover:bg-bg-hover cursor-pointer transition-colors"
                    >
                      <td className="py-2.5 px-4 font-mono font-bold text-text-primary">{t.symbol}</td>
                      <td className="py-2.5 px-4 text-text-secondary text-xs">{t.entry_date}</td>
                      <td className="py-2.5 px-4 text-text-secondary text-xs">{t.exit_date}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-text-primary">₺{t.entry_price.toFixed(2)}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-text-primary">₺{t.exit_price.toFixed(2)}</td>
                      <td className={`py-2.5 px-4 text-right font-mono font-medium ${t.return_pct >= 0 ? "text-signal-up" : "text-signal-down"}`}>
                        {t.return_pct >= 0 ? "+" : ""}{t.return_pct.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Tile({ label, value, sub, tone, icon: Icon, ...rest }) {
  const cls = tone === "up" ? "text-signal-up" : tone === "down" ? "text-signal-down" : "text-text-primary";
  return (
    <div className="bg-bg-panel border border-bg-border rounded-xl p-4" {...rest}>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary flex items-center gap-1">
        {Icon && <Icon size={11} className={cls} />} {label}
      </div>
      <div className={`text-xl font-mono font-bold mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[11px] text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}
